import React from 'react';
import {
  Box, Typography, TextField, Autocomplete, Stack,
  IconButton, Button, Checkbox, FormControlLabel,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  TALMUDA_TRACTATES, TRACTATE_NAMES, EMPTY_RANGE, EMPTY_TRACTATE_ENTRY,
} from './gemaraSelectionModel.js';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import { GemaraSelectionEditorNext } from './GemaraNext.jsx';

export {
  TALMUDA_TRACTATES, TRACTATE_NAMES, EMPTY_RANGE, EMPTY_TRACTATE_ENTRY, selectionToEntries, entriesToSelection, isGemaraEntriesValid,
} from './gemaraSelectionModel.js';

/**
 * עורך בחירת גמרא — מסכתות, טווחי דפים, ואופציונלית דגל נבחן.
 * controlled: value = entries[], onChange(nextEntries)
 */
/** In the new design the same props render GemaraSelectionEditorNext. */
const GemaraSelectionEditor = (props) => {
  const inside = useCkInside();
  return inside ? <GemaraSelectionEditorNext {...props} /> : <GemaraSelectionEditorLegacy {...props} />;
};

const GemaraSelectionEditorLegacy = ({
  value = [],
  onChange,
  showExamined = true,
  title,
}) => {
  const entries = value?.length ? value : [EMPTY_TRACTATE_ENTRY()];

  const setEntries = (next) => {
    onChange?.(typeof next === 'function' ? next(entries) : next);
  };

  const updateTractate = (tIdx, tractate) => {
    setEntries((prev) => prev.map((e, i) => (i === tIdx ? { ...e, tractate } : e)));
  };

  const updateRange = (tIdx, rIdx, field, fieldValue) => {
    setEntries((prev) => prev.map((e, i) => {
      if (i !== tIdx) return e;
      return {
        ...e,
        ranges: e.ranges.map((r, j) => (j === rIdx ? { ...r, [field]: fieldValue } : r)),
      };
    }));
  };

  const addRange = (tIdx) => {
    setEntries((prev) => prev.map((e, i) => (
      i === tIdx ? { ...e, ranges: [...e.ranges, EMPTY_RANGE()] } : e
    )));
  };

  const removeRange = (tIdx, rIdx) => {
    setEntries((prev) => prev.map((e, i) => {
      if (i !== tIdx || e.ranges.length <= 1) return e;
      return { ...e, ranges: e.ranges.filter((_, j) => j !== rIdx) };
    }));
  };

  const addTractateEntry = () => setEntries((prev) => [...prev, EMPTY_TRACTATE_ENTRY()]);

  const removeTractateEntry = (tIdx) => {
    setEntries((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== tIdx)));
  };

  const toggleExamined = (tIdx) => {
    setEntries((prev) => prev.map((e, i) => (
      i === tIdx ? { ...e, examined: !e.examined } : e
    )));
  };

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{title}</Typography>
      )}
      {entries.map((entry, tIdx) => {
        const maxDaf = TALMUDA_TRACTATES[entry.tractate] || 999;
        return (
          <Box key={tIdx} sx={{ mb: 2, p: 1.5, border: '1px solid var(--ck-border, #e0e0e0)', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
              <Autocomplete
                options={TRACTATE_NAMES}
                value={entry.tractate || null}
                onChange={(_, v) => updateTractate(tIdx, v || '')}
                renderInput={(params) => <TextField {...params} label="מסכתא" size="small" />}
                sx={{ minWidth: 180 }}
              />
              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                color="error"
                onClick={() => removeTractateEntry(tIdx)}
                disabled={entries.length <= 1}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
            {entry.ranges.map((range, rIdx) => (
              <Stack
                key={rIdx}
                direction="row"
                spacing={1}
                sx={{ mb: 0.5, alignItems: 'center', pr: 4 }}
              >
                <TextField
                  label="מדף"
                  type="number"
                  size="small"
                  value={range.from}
                  onChange={(e) => updateRange(tIdx, rIdx, 'from', e.target.value)}
                  inputProps={{ min: 2, max: maxDaf }}
                  sx={{ width: 90 }}
                />
                <TextField
                  label="עד דף"
                  type="number"
                  size="small"
                  value={range.to}
                  onChange={(e) => updateRange(tIdx, rIdx, 'to', e.target.value)}
                  inputProps={{ min: 2, max: maxDaf }}
                  sx={{ width: 90 }}
                />
                {entry.tractate && maxDaf < 999 && (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {`(עד ${maxDaf})`}
                  </Typography>
                )}
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeRange(tIdx, rIdx)}
                  disabled={entry.ranges.length <= 1}
                >
                  <DeleteOutlineIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" onClick={() => addRange(tIdx)} disabled={!entry.tractate}>
              + טווח דפים נוסף
            </Button>
            {showExamined && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!entry.examined}
                    onChange={() => toggleExamined(tIdx)}
                    size="small"
                    color="success"
                  />
                }
                label="נבחן במסכת זו"
                sx={{ mt: 0.5, ml: 0, display: 'flex' }}
              />
            )}
          </Box>
        );
      })}
      <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addTractateEntry}>
        הוסף מסכתא
      </Button>
    </Box>
  );
};

export default GemaraSelectionEditor;
